// components/Cms/AiGenerateModal.js
// ──────────────────────────────────────────────────────────────────────────────
// Reusable modal for prompting the AI generator and previewing the result.
//
// Workflow:
//   1. Admin enters a prompt + tone (and any custom fields the parent passes).
//   2. Clicks "Generate" → calls onGenerate(formValues, { regenerate:false }).
//   3. We render the result via renderPreview(result).
//   4. Admin can: "Regenerate" (same prompt, different output),
//                 "Discard" (close without applying), or
//                 "Use this" → onApply(result).
// ──────────────────────────────────────────────────────────────────────────────

import React, { useState, useCallback, useEffect } from 'react';
import { Sparkles, RefreshCw, Check, X as XIcon, AlertTriangle } from 'lucide-react';
import { AppModal, AppButton, AiModelPicker } from '../ui';
import useAiModels from '../../hooks/useAiModels';

// ── Class strings ─────────────────────────────────────────────────────────
// Converted to Tailwind 2026-08-25 (Phase 5); ai-generate-modal.scss deleted
// with it. The input's focus state was the find:
//
//     &:focus { border-color: var(--brand-primary); }
//
// with no fallback — and --brand-primary is scoped to `.demo-public` in
// DemoClasses, which this modal never renders inside. The declaration was
// invalid at computed-value time and dropped, so focusing a field in the CMS AI
// generator changed nothing at all. Its companion glow fell back to
// `rgba(99, 102, 241, 0.15)`, an indigo belonging to no token. Both are
// --primary now. See lint:tokens --check outofscope, added the same day.
//
// The label rule was a descendant selector (`&__field label`), so it is an
// arbitrary variant on the field that owns it rather than a class per label.
const FIELD =
  "tw-flex tw-flex-col tw-gap-[0.3rem] [&_label]:tw-text-xs [&_label]:tw-font-semibold " +
  "[&_label]:tw-uppercase [&_label]:tw-tracking-[0.04em] [&_label]:tw-text-content-muted";
const INPUT =
  "tw-w-full tw-rounded-sm tw-border tw-border-line tw-bg-surface tw-px-[0.7rem] tw-py-[0.55rem] " +
  "tw-font-[inherit] tw-text-[0.9rem] tw-text-content tw-transition-[border-color,box-shadow] " +
  "tw-duration-150 focus:tw-border-primary focus:tw-outline-none focus:tw-ring-[3px] " +
  "focus:tw-ring-primary/15 disabled:tw-cursor-not-allowed disabled:tw-opacity-60";
const ERROR =
  "tw-flex tw-items-start tw-gap-2 tw-rounded-sm tw-border tw-border-danger/40 " +
  "tw-bg-danger-soft tw-px-[0.7rem] tw-py-[0.55rem] tw-text-[0.85rem] tw-text-danger-fg";

const TONES = [
  { value: 'confident, friendly, modern', label: 'Confident & Friendly' },
  { value: 'professional and authoritative', label: 'Professional' },
  { value: 'warm and conversational', label: 'Warm & Conversational' },
  { value: 'energetic and bold', label: 'Energetic & Bold' },
  { value: 'minimal and matter-of-fact', label: 'Minimal' },
];

/**
 * Props:
 *   show, onHide
 *   title              — modal title (default "Generate with AI")
 *   description        — small helper text under title
 *   defaultPrompt      — pre-fill the prompt textarea
 *   showTone           — show tone selector (default true)
 *   showLength         — show short/medium/long selector (default false)
 *   extraFields        — array of { key, label, type:'text'|'textarea'|'number', placeholder?, required?, value, onChange } rendered above the prompt
 *   modelsUrl         — AI model catalogue endpoint (default '/cms/ai/models').
 *                        Every generator reachable from this modal is a /cms/ai/*
 *                        route, so the default is right for all of them; pass it
 *                        only if you point the modal somewhere else.
 *   onGenerate(values, { regenerate }) → Promise<resultData>
 *                        `values` is { prompt, tone, length, model, reasoningEffort }.
 *                        Forward the last two to the API — they are the admin's
 *                        model pick, and dropping them silently downgrades the
 *                        generation to the server default.
 *   renderPreview(result) → ReactNode
 *   onApply(result)    — admin accepted the result
 *   applyLabel         — button label (default "Use This")
 *   busyHint           — extra hint shown next to spinner
 */
const AiGenerateModal = ({
  show,
  onHide,
  title = 'Generate with AI',
  description = "Describe what you'd like and the AI will draft it. You can regenerate or edit before saving.",
  defaultPrompt = '',
  showTone = true,
  showLength = false,
  extraFields = [],
  onGenerate,
  renderPreview,
  onApply,
  applyLabel = 'Use This',
  modelsUrl = '/cms/ai/models',
}) => {
  const [prompt, setPrompt] = useState(defaultPrompt);
  const [tone, setTone] = useState(TONES[0].value);
  const [length, setLength] = useState('medium');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [usage, setUsage] = useState(null);
  const [elapsed, setElapsed] = useState(0);

  // Only fetched while the modal is open — a page that merely contains this
  // modal has no business pulling a catalogue nobody has asked to see.
  const ai = useAiModels(modelsUrl, show);

  // Reset state when modal opens fresh
  React.useEffect(() => {
    if (show) {
      setPrompt(defaultPrompt);
      setError(null);
      setResult(null);
      setUsage(null);
    }
  }, [show, defaultPrompt]);

  const run = useCallback(
    async (regenerate) => {
      setBusy(true);
      setError(null);
      try {
        const values = { prompt, tone, length, ...ai.payload };
        const res = await onGenerate(values, { regenerate });
        setResult(res?.data || res);
        setUsage(res?.usage || null);
      } catch (err) {
        const detail =
          err?.response?.data?.message ||
          err?.message ||
          'Generation failed. Please try again.';
        const code = err?.response?.data?.code;
        if (code === 'quota_exhausted') {
          setError('You have used your monthly AI quota. It resets at the start of next month, or upgrade your plan for more.');
        } else if (code === 'feature_disabled' || code === 'no_plan') {
          setError(detail);
        } else if (err?.response?.status === 429) {
          setError('Too many AI requests. Please wait a few minutes and try again.');
        } else {
          setError(detail);
        }
      } finally {
        setBusy(false);
      }
    },
    [prompt, tone, length, ai.payload, onGenerate]
  );

  /* A reasoning model on a long piece is minutes, not seconds. A spinner that
     never changes reads as a hung request, and an admin who reloads the page
     pays for a draft they will never see — so the wait is counted out loud. */
  useEffect(() => {
    if (!busy) { setElapsed(0); return undefined; }
    const started = Date.now();
    const id = setInterval(() => setElapsed(Math.round((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(id);
  }, [busy]);

  const handleApply = () => {
    if (!result) return;
    onApply?.(result);
    onHide?.();
  };

  return (
    <AppModal
      show={show}
      onHide={onHide}
      title={
        <span className="tw-inline-flex tw-items-center tw-gap-[0.4rem]">
          <Sparkles size={16} /> {title}
        </span>
      }
      size="lg"
      footer={
        <div className="tw-flex tw-w-full tw-justify-end tw-gap-2">
          <AppButton variant="ghost" onClick={onHide} disabled={busy}>
            <XIcon size={14} /> Discard
          </AppButton>
          {result ? (
            <>
              <AppButton variant="secondary" onClick={() => run(true)} disabled={busy}>
                <RefreshCw size={14} className={busy ? 'tw-animate-spin' : ''} /> Regenerate
              </AppButton>
              <AppButton variant="default" onClick={handleApply} disabled={busy}>
                <Check size={14} /> {applyLabel}
              </AppButton>
            </>
          ) : (
            <AppButton variant="default" onClick={() => run(false)} disabled={busy}>
              <Sparkles size={14} className={busy ? 'tw-animate-spin' : ''} />
              {busy ? 'Generating…' : 'Generate'}
            </AppButton>
          )}
        </div>
      }
    >
      <div className="tw-flex tw-flex-col tw-gap-[0.85rem]">
        {description && <p className="tw-mb-1 tw-mt-0 tw-text-[0.85rem] tw-leading-[1.45] tw-text-content-muted">{description}</p>}

        {/* Custom fields (e.g. blog topic, syllabus days) */}
        {extraFields.map((f) => (
          <div className={FIELD} key={f.key}>
            <label htmlFor={`ai-${f.key}`}>
              {f.label}
              {f.required && <span className="tw-ms-[0.2rem] tw-text-danger">*</span>}
            </label>
            {f.type === 'textarea' ? (
              <textarea
                id={`ai-${f.key}`}
                className={INPUT}
                rows={f.rows || 3}
                placeholder={f.placeholder || ''}
                value={f.value || ''}
                onChange={(e) => f.onChange(e.target.value)}
                disabled={busy}
              />
            ) : (
              <input
                id={`ai-${f.key}`}
                type={f.type || 'text'}
                className={INPUT}
                placeholder={f.placeholder || ''}
                value={f.value || ''}
                onChange={(e) => f.onChange(e.target.value)}
                disabled={busy}
                min={f.min}
                max={f.max}
              />
            )}
            {f.hint && <small className="tw-text-[0.72rem] tw-text-content-subtle">{f.hint}</small>}
          </div>
        ))}

        {/* Prompt */}
        <div className={FIELD}>
          <label htmlFor="ai-prompt">Your brief</label>
          <textarea
            id="ai-prompt"
            className={INPUT}
            rows={4}
            placeholder="e.g. We're a Bangalore-based fullstack bootcamp focused on placement-ready React + Node engineers. Emphasise live projects and 1:1 mentorship."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            disabled={busy}
          />
          <small className="tw-text-[0.72rem] tw-text-content-subtle">Optional. The more specific, the better the result.</small>
        </div>

        <div className="tw-flex tw-flex-wrap tw-gap-[0.85rem]">
          {showTone && (
            <div className={`${FIELD} tw-min-w-[200px] tw-flex-1`}>
              <label htmlFor="ai-tone">Tone</label>
              <select
                id="ai-tone"
                className={INPUT}
                value={tone}
                onChange={(e) => setTone(e.target.value)}
                disabled={busy}
              >
                {TONES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          )}
          {showLength && (
            <div className={`${FIELD} tw-min-w-[200px] tw-flex-1`}>
              <label htmlFor="ai-length">Length</label>
              <select
                id="ai-length"
                className={INPUT}
                value={length}
                onChange={(e) => setLength(e.target.value)}
                disabled={busy}
              >
                <option value="short">Short (~400 words)</option>
                <option value="medium">Medium (~700 words)</option>
                <option value="long">Long (~1000 words)</option>
              </select>
            </div>
          )}
        </div>

        {/* Which model writes it. Left alone this is the server's recommended
            model, which is a reasoning model — the institute's configured chat
            model is tuned for answering one student's doubt, not for a 1,000
            word post or a 40-day syllabus. */}
        <AiModelPicker {...ai} className="tw-mt-[0.9rem]" />

        {error && (
          <div className={ERROR}>
            <AlertTriangle size={14} /> {error}
          </div>
        )}

        {busy && !result && (
          <div className="tw-flex tw-items-center tw-gap-[0.55rem] tw-rounded-sm tw-bg-surface-alt tw-px-[0.85rem] tw-py-[0.7rem] tw-text-[0.85rem] tw-text-content-muted">
            <RefreshCw size={16} className="tw-animate-spin" />
            <span>
              Writing with {ai.activeModel?.label || 'AI'}
              {elapsed > 0 && ` · ${elapsed}s`}
              {ai.activeModel?.reasoning
                ? ' — this model thinks before it answers, so a minute or two is normal.'
                : '…'}
            </span>
          </div>
        )}

        {result && (
          <div className="tw-overflow-hidden tw-rounded-md tw-border tw-border-line tw-bg-surface-alt">
            <div className="tw-flex tw-items-center tw-justify-between tw-border-0 tw-border-b tw-border-solid tw-border-line tw-bg-surface-sunken tw-px-[0.85rem] tw-py-[0.55rem] tw-text-[0.85rem]">
              <strong>AI Suggestion</strong>
              {usage?.tokensUsed != null && (
                <span className="tw-text-xs tw-text-content-muted">~{usage.tokensUsed.toLocaleString()} tokens</span>
              )}
            </div>
            <div className="tw-max-h-[50vh] tw-overflow-auto tw-p-[0.85rem] tw-text-content">
              {renderPreview ? renderPreview(result) : (
                <pre className="tw-m-0 tw-whitespace-pre-wrap tw-text-[0.78rem]">{JSON.stringify(result, null, 2)}</pre>
              )}
            </div>
          </div>
        )}
      </div>
    </AppModal>
  );
};

export default AiGenerateModal;
